import { useCallback, useMemo, useRef, useState, type ReactElement } from "react";
import { Check, ChevronRight, Lock } from "lucide-react";

import { useLocalization } from "@i18n";

import type {
  InspectionConservationStateSelection,
  InspectionConservationStateColumnKey,
  InspectionDetail,
  InspectionParticipant,
} from "../../api/inspection";
import { isInspectionFormReadOnly } from "../../utils/inspectionFormAccess";
import type { BindInspectionStepSave } from "../../utils/useBindInspectionStepSave";
import { ecItemKey } from "../inspection-ec-columns";
import type { InspectionFormBundleProps } from "../inspection-form-bundle";
import {
  canFinalizeInspection,
  unlockedInspectionStepCount,
} from "./inspection-step-completion";
import { INSPECTION_FORM_STEPS } from "./inspection-steps";
import InspectionAccessesStep from "./steps/InspectionAccessesStep";
import InspectionBeachConditionStep from "./steps/InspectionBeachConditionStep";
import InspectionConservationStateStep from "./steps/InspectionConservationStateStep";
import InspectionDamBodyStep from "./steps/InspectionDamBodyStep";
import InspectionFinalizeConfirmModal from "./steps/InspectionFinalizeConfirmModal";
import InspectionGeneralCommentsStep from "./steps/InspectionGeneralCommentsStep";
import InspectionGeneralDataStep from "./steps/InspectionGeneralDataStep";
import InspectionInstrumentationStep from "./steps/InspectionInstrumentationStep";
import InspectionParticipantsStep from "./steps/InspectionParticipantsStep";
import InspectionReservoirStep from "./steps/InspectionReservoirStep";
import InspectionSignatureModal from "./steps/InspectionSignatureModal";
import InspectionSpillwayStep from "./steps/InspectionSpillwayStep";
import InspectionTailingsWaterIntakeStep from "./steps/InspectionTailingsWaterIntakeStep";
import type { InspectionChecklistStepWrapperProps } from "./steps/checklistStep.props";
import "../../styles/inspection-ui.css";

const CHECKLIST_STEP_COMPONENTS: Record<
  string,
  (props: InspectionChecklistStepWrapperProps) => ReactElement
> = {
  accesses: InspectionAccessesStep,
  damBody: InspectionDamBodyStep,
  reservoir: InspectionReservoirStep,
  spillway: InspectionSpillwayStep,
  instrumentation: InspectionInstrumentationStep,
  beachCondition: InspectionBeachConditionStep,
  tailingsWaterIntake: InspectionTailingsWaterIntakeStep,
};

const EC_COLUMN_KEYS: InspectionConservationStateColumnKey[] = [
  "ecK",
  "ecL",
  "ecM",
  "ecN",
  "ecO",
];

const LAST_STEP_INDEX = INSPECTION_FORM_STEPS.length - 1;

/** Seleções ∑EC já persistidas na ficha, no shape que o step consome. */
const toConservationStateSelections = (
  detail: InspectionDetail,
): InspectionConservationStateSelection[] =>
  EC_COLUMN_KEYS.flatMap((columnKey) => {
    const response = detail.itemResponses.find(
      (row) => row.itemKey === ecItemKey(columnKey),
    );
    const score = (response?.answerValue as { selectedScore?: unknown } | null)
      ?.selectedScore;

    if (typeof score !== "number") return [];

    return [
      {
        columnKey,
        selectedScore: score,
      },
    ];
  });

/**
 * Ficha `inspection-v1` (form §7.1) — trilha vertical de steps como no
 * protótipo; regras de desbloqueio/finalização seguem o PRD desta versão.
 * Nova inspeção (cabeçalho / POST Y=4) não passa por aqui.
 */
const InspectionV1Form = ({
  structureId,
  mode,
  capabilities,
  detail,
  onDetailChange,
  onRefetchDetail,
  onClose,
}: InspectionFormBundleProps) => {
  const { translate } = useLocalization();
  const formSteps = useMemo(
    () =>
      INSPECTION_FORM_STEPS.map((step) => ({
        ...step,
        label: translate(step.label),
      })),
    [translate],
  );

  const [activeStep, setActiveStep] = useState(0);
  const [signingParticipant, setSigningParticipant] =
    useState<InspectionParticipant | null>(null);
  const [isFinalizeOpen, setIsFinalizeOpen] = useState(false);
  const stepSaveRef = useRef<(() => void) | null>(null);

  const bindSave = useCallback<BindInspectionStepSave>((save) => {
    stepSaveRef.current = save;
  }, []);

  const unlockedSteps = unlockedInspectionStepCount(
    detail.itemResponses,
    detail.attachments,
  );

  const isReadOnly = isInspectionFormReadOnly(mode, capabilities, detail);
  const canWrite = !isReadOnly;
  const navigableSteps = isReadOnly ? INSPECTION_FORM_STEPS.length : unlockedSteps;

  const replaceParticipants = (participants: InspectionParticipant[]) => {
    onDetailChange({ ...detail, participants });
  };

  const handleSaved = (saved: InspectionDetail) => {
    onDetailChange(saved);
    setActiveStep((current) =>
      Math.min(
        current + 1,
        unlockedInspectionStepCount(saved.itemResponses, saved.attachments) - 1,
        LAST_STEP_INDEX,
      ),
    );
  };

  const renderStepPanel = (stepKey: string) => {
    const checklistProps: InspectionChecklistStepWrapperProps = {
      structureId,
      inspectionId: detail.id,
      itemResponses: detail.itemResponses,
      attachments: detail.attachments,
      inspectionStatus: detail.inspectionStatus,
      canWrite,
      onSaved: handleSaved,
      onDetailSync: onDetailChange,
      onBindSave: bindSave,
    };

    const ChecklistStep = CHECKLIST_STEP_COMPONENTS[stepKey];
    if (ChecklistStep) return <ChecklistStep {...checklistProps} />;

    if (stepKey === "generalData") {
      return (
        <InspectionGeneralDataStep
          structureId={structureId}
          detail={detail}
          canWrite={canWrite}
          onSaved={handleSaved}
          onParticipantsChange={replaceParticipants}
          onBindSave={bindSave}
        />
      );
    }

    if (stepKey === "conservationState") {
      return (
        <InspectionConservationStateStep
          structureId={structureId}
          inspectionId={detail.id}
          selections={toConservationStateSelections(detail)}
          canWrite={canWrite}
          onSaved={handleSaved}
          onBindSave={bindSave}
        />
      );
    }

    if (stepKey === "generalComments") {
      return <InspectionGeneralCommentsStep {...checklistProps} />;
    }

    return (
      <InspectionParticipantsStep
        structureId={structureId}
        inspectionId={detail.id}
        participants={detail.participants}
        canWrite={canWrite}
        inspectionStatus={detail.inspectionStatus}
        mode="sign"
        onSign={setSigningParticipant}
        onSaved={replaceParticipants}
      />
    );
  };

  const activeStepDef = formSteps[activeStep];
  const isLastStep = activeStep === LAST_STEP_INDEX;
  const signedCount =
    detail.participants?.filter((participant) => participant.hasSignature)
      .length ?? 0;

  return (
    <>
      <div className="insp-ficha-shell">
        <div className="insp-ficha-wrap">
          <nav
            role="tablist"
            aria-label={translate("inspection.form.stepsHeading")}
            className="insp-step-rail"
            id="step-rail"
          >
            <div className="insp-step-rail-head">
              {translate("inspection.form.stepsHeading")}
            </div>
            {formSteps.map((step, index) => {
              const isLocked = index >= navigableSteps;
              const isActive = index === activeStep;
              const isDone = !isActive && !isLocked && index < unlockedSteps - 1;

              return (
                <button
                  key={step.stepKey}
                  type="button"
                  role="tab"
                  id={`inspection-tab-${step.stepKey}`}
                  aria-controls={`inspection-panel-${step.stepKey}`}
                  aria-selected={isActive}
                  aria-disabled={isLocked}
                  data-locked={isLocked}
                  disabled={isLocked}
                  aria-label={step.label}
                  title={isLocked ? translate("inspection.form.stepLocked") : undefined}
                  onClick={() => setActiveStep(index)}
                  className={[
                    "insp-step-rail-item",
                    isActive ? "is-active" : "",
                    isDone ? "is-done" : "",
                    isLocked ? "is-locked" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  <span className="insp-step-num" aria-hidden>
                    {isLocked ? (
                      <Lock size={13} />
                    ) : isDone ? (
                      <Check size={13} />
                    ) : (
                      index + 1
                    )}
                  </span>
                  <span className="insp-step-lbl" aria-hidden>
                    {step.label}
                  </span>
                </button>
              );
            })}
          </nav>

          <div className="insp-ficha-body">
            {activeStepDef ? (
              <>
                <h3 className="insp-ficha-step-title">
                  {`${activeStep + 1}. ${activeStepDef.label}`}
                </h3>
                <p className="insp-ficha-step-sub">
                  {translate("inspection.form.stepOf", {
                    current: activeStep + 1,
                    total: INSPECTION_FORM_STEPS.length,
                  })}
                </p>
                <div
                  role="tabpanel"
                  id={`inspection-panel-${activeStepDef.stepKey}`}
                  aria-labelledby={`inspection-tab-${activeStepDef.stepKey}`}
                >
                  {renderStepPanel(activeStepDef.stepKey)}
                </div>
              </>
            ) : null}
          </div>
        </div>

        <div className="insp-ficha-foot">
          <button
            type="button"
            className="insp-btn insp-btn-outline"
            onClick={onClose}
          >
            {isReadOnly
              ? translate("inspection.actions.close")
              : translate("inspection.actions.cancel")}
          </button>
          <span className="insp-foot-spacer" />
          {activeStep > 0 ? (
            <button
              type="button"
              className="insp-btn insp-btn-outline"
              onClick={() => setActiveStep((current) => Math.max(0, current - 1))}
            >
              {translate("inspection.actions.back")}
            </button>
          ) : null}
          {isReadOnly && !isLastStep ? (
            <button
              type="button"
              className="insp-btn insp-btn-outline"
              onClick={() =>
                setActiveStep((current) => Math.min(LAST_STEP_INDEX, current + 1))
              }
            >
              {translate("inspection.actions.next")}
            </button>
          ) : null}
          {canWrite && !isLastStep ? (
            <button
              type="button"
              className="insp-btn insp-btn-primary"
              onClick={() => stepSaveRef.current?.()}
            >
              {translate("inspection.form.saveAndContinue")}
              <ChevronRight aria-hidden size={18} />
            </button>
          ) : null}
          {canWrite && isLastStep ? (
            <button
              type="button"
              disabled={!canFinalizeInspection(detail)}
              className="insp-btn insp-btn-primary disabled:opacity-50"
              onClick={() => setIsFinalizeOpen(true)}
            >
              {translate("inspection.actions.finalize")}
            </button>
          ) : null}
        </div>
      </div>

      {signingParticipant ? (
        <InspectionSignatureModal
          isOpen
          onClose={() => setSigningParticipant(null)}
          structureId={structureId}
          inspectionId={detail.id}
          participant={signingParticipant}
          inspectionStatus={detail.inspectionStatus}
          onSigned={() => {
            const signedId = signingParticipant.id;
            setSigningParticipant(null);
            onDetailChange({
              ...detail,
              participants: detail.participants.map((participant) =>
                participant.id === signedId
                  ? { ...participant, hasSignature: true }
                  : participant,
              ),
            });
            onRefetchDetail();
          }}
        />
      ) : null}

      <InspectionFinalizeConfirmModal
        isOpen={isFinalizeOpen}
        onClose={() => setIsFinalizeOpen(false)}
        structureId={structureId}
        inspectionId={detail.id}
        inspectionCode={detail.inspectionCode}
        inspectionDate={detail.inspectionDate}
        signedCount={signedCount}
        totalParticipants={detail.participants?.length ?? 0}
        anomalyCount={detail.anomalyCount}
        conservationStateScore={detail.conservationStateScore}
        onFinalized={(finalized) => {
          onDetailChange({
            ...detail,
            inspectionStatus: finalized.inspectionStatus,
            finalizedAt: finalized.finalizedAt,
            hasPdf: finalized.hasPdf,
            pdfVerificationCode: finalized.pdfVerificationCode,
            conservationStateScore: finalized.conservationStateScore,
            anomalyCount: finalized.anomalyCount,
          });
          setIsFinalizeOpen(false);
        }}
      />
    </>
  );
};

export default InspectionV1Form;
